import { AlertTriangle, Heart } from "lucide-react";

const C = {
  surface: "#111827",
  surfaceAlt: "#0f172a",
  border: "#1e293b",
  muted: "#6b7280",
  text: "#e2e8f0",
  mutedText: "#94a3b8",
  red: "#ef4444",
  yellow: "#fbbf24",
  orange: "#f97316",
};

const REASON_ICON = {
  "Injury": "🩹",
  "Muscular": "💪",
  "Knock": "🦵",
  "Illness": "🤒",
  "Suspension": "🟨",
  "Unknown": "❓",
};

function getReasonIcon(reason) {
  if (!reason) return "🩹";
  const r = reason.toLowerCase();
  if (r.includes("muscl") || r.includes("hamstring") || r.includes("thigh")) return "💪";
  if (r.includes("suspen") || r.includes("ban")) return "🟨";
  if (r.includes("ill") || r.includes("sick")) return "🤒";
  if (r.includes("knee") || r.includes("ankle") || r.includes("leg")) return "🦵";
  return "🩹";
}

function groupByTeam(injuries) {
  const groups = {};
  for (const inj of injuries) {
    const teamName = inj.team?.name || "Squadra sconosciuta";
    const teamId = inj.team?.id || teamName;
    const logo = inj.team?.logo;
    if (!groups[teamId]) {
      groups[teamId] = { name: teamName, logo, players: [] };
    }
    groups[teamId].players.push({
      name: inj.player?.name || "N/D",
      reason: inj.player?.reason || inj.player?.type || "Infortunio",
      type: inj.player?.type || "",
    });
  }
  return Object.values(groups);
}

/**
 * InjuriesPanel — shows injured/suspended players grouped by team.
 * Data comes from the injuries array in the match API response.
 */
export function InjuriesPanel({ injuries, homeTeamId, awayTeamId, className = "" }) {
  if (!injuries || injuries.length === 0) {
    return (
      <div
        data-testid="injuries-panel"
        className={className}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: "16px 20px",
        }}
      >
        <h3
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: C.muted,
            margin: "0 0 10px 0",
          }}
        >
          Infortuni e Squalifiche
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.muted, fontSize: 13 }}>
          <Heart size={14} />
          <span>Nessun infortunio noto per questa partita</span>
        </div>
      </div>
    );
  }

  const teams = groupByTeam(injuries);

  return (
    <div
      data-testid="injuries-panel"
      className={className}
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: "16px 20px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <AlertTriangle size={14} color={C.orange} />
        <h3
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: C.muted,
            margin: 0,
          }}
        >
          Infortuni e Squalifiche ({injuries.length})
        </h3>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {teams.map((team, ti) => (
          <div key={ti}>
            {/* Team header */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              {team.logo && (
                <img
                  src={team.logo}
                  alt={team.name}
                  style={{ width: 18, height: 18, objectFit: "contain" }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.text,
                  letterSpacing: "0.04em",
                }}
              >
                {team.name}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: C.muted,
                  background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))",
                  padding: "1px 6px",
                  borderRadius: 10,
                }}
              >
                {team.players.length}
              </span>
            </div>

            {/* Players */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {team.players.map((p, pi) => {
                const isSuspension = (p.reason || "").toLowerCase().includes("suspen") ||
                  (p.reason || "").toLowerCase().includes("ban");
                const accentColor = isSuspension ? C.yellow : C.red;
                const icon = getReasonIcon(p.reason);
                return (
                  <div
                    key={pi}
                    title={p.reason}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      background: isSuspension
                        ? "rgba(251,191,36,0.06)"
                        : "rgba(239,68,68,0.06)",
                      border: `1px solid ${isSuspension ? "rgba(251,191,36,0.2)" : "rgba(239,68,68,0.2)"}`,
                      borderRadius: 8,
                      padding: "4px 8px",
                      maxWidth: "100%",
                    }}
                  >
                    <span style={{ fontSize: 12 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: C.text, lineHeight: 1.2 }}>
                        {p.name}
                      </div>
                      {p.reason && (
                        <div style={{ fontSize: 10, color: accentColor, opacity: 0.85 }}>
                          {p.reason}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {ti < teams.length - 1 && (
              <div style={{ height: 1, background: C.border, marginTop: 14 }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
